/**
 * <Score> — render a small SVG of staff notation via VexFlow.
 *
 * VexFlow is loaded *lazily inside the effect* — never at module scope —
 * so consumers that import the package eagerly still get the heavy
 * library code-split out of their initial chunk. Pair with
 * `React.lazy(() => import("@music-app/notation").then(m => ({ default: m.Score })))`
 * to keep the package itself out of the entry chunk too.
 *
 * `aria-label` is required at the type level: notation by itself is
 * inaccessible to screen readers, so every <Score> ships with prose
 * describing what it depicts (per phase-2-spec §7.4).
 */

import { type ReactNode, useEffect, useRef } from "react";
import type { VexClef, VexNote } from "./notation-types.js";

export interface ScoreProps {
  /** Notes to render, left-to-right on a single stave. */
  notes: VexNote[];
  /** Clef for the stave. Defaults to treble. */
  clef?: VexClef;
  /**
   * Plain-prose summary of the music ("C major scale, ascending"). Must
   * be supplied — required at the type level for accessibility.
   */
  "aria-label": string;
  /** SVG render width in pixels. Defaults to 400. */
  width?: number;
  /** SVG render height in pixels. Defaults to 120. */
  height?: number;
}

export function Score(props: ScoreProps): ReactNode {
  const { notes, clef = "treble", width = 400, height = 120 } = props;
  const ariaLabel = props["aria-label"];
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    const container = containerRef.current;
    if (!container) return;

    void (async () => {
      // `vexflow/core` ships VexFlow's logic without any music-font
      // glyph data; the Bravura glyph table comes through a separate
      // dynamic import so Vite splits it into its own chunk. This is
      // the "tree-shaken vexflow-core entry" escape hatch the
      // phase-2 spec calls out (§7.3 + §18 risk row); without it,
      // the default `vexflow` entry pulls every font (~315 KB gz)
      // and pins the notation chunk well above budget. Bravura is
      // the SMuFL reference engraving font and the only one we ship.
      const [Vex, BravuraFont] = await Promise.all([
        import("vexflow/core"),
        import("vexflow/font/bravura"),
      ]);
      if (cancelled) return;
      if (!containerRef.current) return;

      const { Renderer, Stave, StaveNote, Formatter, Accidental, Flow } = Vex;
      // fetchMusicFont is idempotent (no-ops once a font is loaded).
      await Flow.fetchMusicFont("Bravura", BravuraFont.Font);
      if (cancelled || !containerRef.current) return;
      Flow.setMusicFont("Bravura");

      container.innerHTML = "";

      const renderer = new Renderer(container, Renderer.Backends.SVG);
      renderer.resize(width, height);
      const context = renderer.getContext();

      const stave = new Stave(10, 20, width - 20);
      stave.addClef(clef);
      stave.setContext(context).draw();

      const staveNotes = notes.map((n) => {
        const note = new StaveNote({ keys: n.keys, duration: n.duration, clef });
        n.keys.forEach((key, idx) => {
          const acc = parseAccidental(key);
          if (acc !== null) {
            note.addModifier(new Accidental(acc), idx);
          }
        });
        return note;
      });

      if (staveNotes.length > 0) {
        Formatter.FormatAndDraw(context, stave, staveNotes);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [notes, clef, width, height]);

  return (
    <div
      ref={containerRef}
      role="img"
      aria-label={ariaLabel}
      className="notation-score"
    />
  );
}

/**
 * Pull the accidental token out of a VexFlow key like `"c#/4"` →
 * `"#"`, `"bb/3"` → `"bb"`, `"f/4"` → `null`. The two-character
 * forms are matched first so `"##"` doesn't tokenize as `"#"` plus
 * a stray `#`.
 */
function parseAccidental(key: string): string | null {
  const match = /^[a-g](##|bb|#|b|n)?\//.exec(key);
  if (!match || !match[1]) return null;
  return match[1];
}
